import React, { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import axios from 'axios';
import { apiClient } from '../../api/client';
import type { PatientMedicalOrganization } from '../../constants/patient';
import { intakeFieldSlot } from './intakeFormLayout';

type Props = {
  kind: 'referral' | 'family_doctor';
  value: PatientMedicalOrganization | null;
  onChange: (org: PatientMedicalOrganization | null) => void;
  onPhoneSuggest?: (phone: string) => void;
  placeholder?: string;
  /** ダイアログ見出し等に使う名称（例: 紹介元 / かかりつけ医） */
  entityLabel?: string;
};

/**
 * 紹介元・かかりつけ医の選択フィールド。
 * - 基本は既存マスタからの「選択」
 * - 隣の「新規追加」ボタンから、その場で機関を登録して選択できる
 */
const OrganizationAutocomplete: React.FC<Props> = ({
  kind,
  value,
  onChange,
  onPhoneSuggest,
  placeholder,
  entityLabel,
}) => {
  const label = entityLabel ?? (kind === 'referral' ? '紹介元' : 'かかりつけ医');
  const [options, setOptions] = useState<PatientMedicalOrganization[]>([]);
  const [inputValue, setInputValue] = useState(value?.name ?? '');
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchOptions = useCallback(
    async (q: string) => {
      setLoading(true);
      try {
        const res = await apiClient.get<PatientMedicalOrganization[]>(
          '/api/patient-organizations',
          { params: { kind, q: q || undefined } }
        );
        setOptions(res.data);
      } catch {
        setOptions([]);
      } finally {
        setLoading(false);
      }
    },
    [kind]
  );

  useEffect(() => {
    void fetchOptions('');
  }, [fetchOptions]);

  useEffect(() => {
    setInputValue(value?.name ?? '');
  }, [value]);

  const applyOrgPhone = (org: PatientMedicalOrganization | null, enteredPhone?: string) => {
    if (!onPhoneSuggest) return;
    const phone = enteredPhone?.trim() || org?.default_phone?.trim() || '';
    onPhoneSuggest(phone);
  };

  const selectOrg = (org: PatientMedicalOrganization | null) => {
    onChange(org);
    applyOrgPhone(org);
  };

  const handleCreated = (org: PatientMedicalOrganization, enteredPhone?: string) => {
    setOptions((prev) => (prev.some((o) => o.id === org.id) ? prev : [org, ...prev]));
    onChange(org);
    applyOrgPhone(org, enteredPhone);
  };

  return (
    <>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <Autocomplete
          fullWidth
          size="small"
          options={options}
          loading={loading}
          value={value}
          inputValue={inputValue}
          onInputChange={(_, v, reason) => {
            setInputValue(v);
            if (reason === 'input') void fetchOptions(v);
            if (reason === 'clear') void fetchOptions('');
          }}
          onChange={(_, v) => selectOrg(v)}
          getOptionLabel={(o) => o.name}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          noOptionsText="新規で登録してください"
          renderInput={(params) => (
            <TextField {...params} {...intakeFieldSlot} placeholder={placeholder ?? '選択'} />
          )}
        />
        <Tooltip title={`${label}を新規追加`}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap', px: 1 }}
          >
            新規
          </Button>
        </Tooltip>
      </Box>

      <AddOrganizationDialog
        open={dialogOpen}
        kind={kind}
        label={label}
        existing={options}
        onClose={() => setDialogOpen(false)}
        onCreated={(org, enteredPhone) => {
          handleCreated(org, enteredPhone);
          setDialogOpen(false);
        }}
      />
    </>
  );
};

type DialogProps = {
  open: boolean;
  kind: 'referral' | 'family_doctor';
  label: string;
  existing: PatientMedicalOrganization[];
  onClose: () => void;
  onCreated: (org: PatientMedicalOrganization, enteredPhone?: string) => void;
};

const AddOrganizationDialog: React.FC<DialogProps> = ({
  open,
  kind,
  label,
  existing,
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ダイアログを開くたびに入力欄を空にする（選択中・入力中の値は引き継がない）
  useEffect(() => {
    if (open) {
      setName('');
      setAddress('');
      setPhone('');
      setError('');
    }
  }, [open]);

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('名称を入力してください');
      return;
    }
    // 既に同名が登録済みならそれを選択
    const dup = existing.find((o) => o.name.trim().toLowerCase() === trimmed.toLowerCase());
    if (dup) {
      onCreated(dup, phone.trim() || undefined);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await apiClient.post<PatientMedicalOrganization>('/api/patient-organizations', {
        kind,
        name: trimmed,
        address: address.trim() || undefined,
        default_phone: phone.trim() || undefined,
      });
      onCreated(res.data, phone.trim() || undefined);
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data?.error) {
        setError(String(e.response.data.error));
      } else {
        setError('登録に失敗しました');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ typography: 'h6' }}>{label}を新規追加</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="名称"
            required
            autoFocus
            fullWidth
            size="small"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label="住所（任意）"
            fullWidth
            size="small"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
          />
          <TextField
            label="電話番号（任意）"
            fullWidth
            size="small"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          {error && (
            <Box sx={{ color: 'error.main', fontSize: '0.8125rem' }}>{error}</Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          キャンセル
        </Button>
        <Button variant="contained" onClick={() => void handleSubmit()} disabled={saving}>
          {saving ? '登録中…' : '追加して選択'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OrganizationAutocomplete;
